import React from 'react';
import SurveyResponseChip from './SurveyResponseChip';
import { SurveyType, WellnessResponse } from '../../../../../shared/constants';
import { surveyScoreToLabel } from '../../../../../shared/surveys';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBadgeCheck } from '@fortawesome/pro-duotone-svg-icons';
import { Box, Paper, Tooltip, Typography } from '@mui/material';
import { dimGrey, lightBlack, lightPurple } from '../../../../../mainTheme';

export const DWS_TOOLTIP = 'Daily wellness survey';

interface DWSCardProps {
  data: {
    // Latest wellness label ('Good') or raw 1-5 score from the overview API.
    lastResponse: string | number | null | undefined;
    // Survey question from the API (`survey_responses[].title`); falls back
    // to DWS_TOOLTIP when the backend does not send it.
    title?: string | null;
  };
  isSelected?: boolean;
  onClick?: () => void;
}

// Legend order matches CSATCard: worst at the top, best at the bottom.
const dwsOptions = [
  { label: WellnessResponse.VeryBad },
  { label: WellnessResponse.Bad },
  { label: WellnessResponse.Neutral },
  { label: WellnessResponse.Good },
  { label: WellnessResponse.VeryGood },
];

const DWSCard: React.FC<DWSCardProps> = ({ data, isSelected, onClick }) => {
  const current = surveyScoreToLabel(
    SurveyType.HubDailyWellnessCheck,
    data.lastResponse,
  );
  const tooltip = data.title || DWS_TOOLTIP;

  return (
    <Paper
      elevation={isSelected ? 8 : 1}
      sx={{
        px: '24px',
        py: '21px',
        cursor: 'pointer',
        transition: 'all 0.3s',
        transform: isSelected ? 'scale(1.02)' : 'none',
        '&:hover': {
          elevation: 3,
        },
        borderRadius: '8px',
        border: isSelected
          ? `1px solid ${lightPurple}`
          : '1px solid var(--Outline-O5---Separator, #D4D4D9)',
        boxShadow: isSelected
          ? '-4px 8px 24px 0px rgba(0, 0, 0, 0.25)'
          : 'none',
        height: '152px',
        flex: '1 0 0',
        overflow: 'hidden',
      }}
      onClick={onClick}
      data-testid="dws-card"
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'space-between',
        }}
      >
        {/* Left section: title+icon */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            justifyContent: 'space-between',
          }}
        >
          <Tooltip title={tooltip} placement="top" arrow>
            <Box sx={{ display: 'flex', width: 'fit-content' }}>
              <Typography
                sx={{
                  color: isSelected ? lightBlack : dimGrey,
                  fontSize: '16px',
                  fontWeight: 700,
                  letterSpacing: '0.2px',
                  mr: 1,
                }}
                aria-label={tooltip}
              >
                Wellness
              </Typography>
              <FontAwesomeIcon
                icon={faBadgeCheck}
                color={isSelected ? lightPurple : dimGrey}
                fontSize={22}
              />
            </Box>
          </Tooltip>
        </Box>
        {/* Right section: 1-5 legend with the latest response highlighted */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-end',
            }}
          >
            {dwsOptions.map((opt) => (
              <SurveyResponseChip
                key={opt.label}
                response={opt.label}
                isCurrent={current === opt.label}
                style={{ marginBottom: '2px', height: 20 }}
                highlightBorder
              />
            ))}
          </div>
        </Box>
      </Box>
    </Paper>
  );
};

export default DWSCard;
